"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * SectionNav
 *
 * Shared quick-navigation for long, sectioned pages (e.g. the legal pages —
 * Privacy Policy and Terms of Use). Renders in two variants:
 *
 *  - variant="rail"  : a sticky side rail (large screens) that highlights the
 *                      section currently in view via a scroll-spy
 *                      (IntersectionObserver).
 *  - variant="inline": a collapsible "On this page" card (smaller screens) that
 *                      expands to the same list of links.
 *
 * Smooth scrolling is handled per-click with `scrollIntoView` (scoped to this
 * component, so no global `scroll-behavior` rule is needed). Anchor `href`s stay
 * valid for no-JS / open-in-new-tab fallbacks. Styling uses the project's m3
 * typography utilities and design tokens.
 *
 * @param {{id: string, title: string}[]} sections  Sections to link to.
 * @param {"rail"|"inline"} variant                 Layout variant.
 * @param {string} ariaLabel                        Accessible label for the nav.
 * @param {string} className                        Additional classes.
 */
export default function SectionNav(t0) {
  const $ = _c(32);
  const {
    sections: t1,
    variant: t2,
    ariaLabel: t3,
    className: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const sections = t5;
  const variant = t2 === undefined ? "rail" : t2;
  const ariaLabel = t3 === undefined ? "Section navigation" : t3;
  const className = t4 === undefined ? "" : t4;
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [open, setOpen] = useState(false);
  const suppressSpyRef = useRef(false);
  const releaseTimerRef = useRef(null);
  let t6;
  let t7;
  if ($[2] !== sections) {
    t6 = () => {
      if (!sections.length) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (suppressSpyRef.current) {
          return;
        }
        const visible = entries.filter(_temp).sort(_temp2);
        if (visible.length) {
          setActiveId(visible[0].target.id);
        }
      }, {
        rootMargin: "-80px 0px -66% 0px",
        threshold: 0
      });
      sections.forEach(t8 => {
        const {
          id
        } = t8;
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
        }
      });
      return () => observer.disconnect();
    };
    t7 = [sections];
    $[2] = sections;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  if (!sections.length) {
    return null;
  }
  let t8;
  let t9;
  if ($[5] !== activeId || $[6] !== sections) {
    const handleClick = (e, id_0) => {
      const target = document.getElementById(id_0);
      if (!target) {
        return;
      }
      e.preventDefault();
      suppressSpyRef.current = true;
      setActiveId(id_0);
      setOpen(false);
      target.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      if (typeof history !== "undefined") {
        history.replaceState(null, "", `#${id_0}`);
      }
      const release = () => {
        suppressSpyRef.current = false;
      };
      window.addEventListener("scrollend", release, {
        once: true
      });
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = setTimeout(release, 1000);
    };
    t8 = "flex flex-col gap-0.5";
    t9 = sections.map(section => {
      const isActive = section.id === activeId;
      return <li key={section.id}><a href={`#${section.id}`} onClick={e_0 => handleClick(e_0, section.id)} aria-current={isActive ? "true" : undefined} className={`block border-l-2 py-1.5 pl-3 m3-label-large no-underline transition-colors ${isActive ? "border-[var(--colorPrimaryPrimary,#004bee)] text-[var(--textLinkOnSurfaceLink,#004bee)]" : "border-transparent text-[var(--textSecondarySecondary,#5e5f59)] hover:text-[var(--textPrimaryPrimary,#1b1c17)]"}`}>{section.title}</a></li>;
    });
    $[5] = activeId;
    $[6] = sections;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t8 || $[10] !== t9) {
    t10 = <ul className={t8}>{t9}</ul>;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const linkList = t10;
  if (variant === "rail") {
    const t11 = `sticky top-[88px] max-h-[calc(100vh-104px)] w-60 flex-shrink-0 overflow-y-auto ${className}`;
    let t12;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <p className="mb-3 m3-title-small text-[var(--textPrimaryPrimary,#1b1c17)]">On this page</p>;
      $[12] = t12;
    } else {
      t12 = $[12];
    }
    let t13;
    if ($[13] !== ariaLabel || $[14] !== linkList || $[15] !== t11) {
      t13 = <nav aria-label={ariaLabel} className={t11}>{t12}{linkList}</nav>;
      $[13] = ariaLabel;
      $[14] = linkList;
      $[15] = t11;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    return t13;
  }
  const t11 = `rounded-2xl border border-[var(--colorOutlineOutline,#aeb7f9)] bg-[var(--colorSurfaceSurfaceVar,#f1f5ff)] ${className}`;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setOpen(_temp3);
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = `transition-transform ${open ? "rotate-180" : ""}`;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: "var(--textSecondarySecondary, #5e5f59)"
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t13) {
    t15 = <ExpandMoreIcon aria-hidden="true" className={t13} sx={t14} />;
    $[19] = t13;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== open || $[22] !== t15) {
    t16 = <button type="button" onClick={t12} aria-expanded={open} className="flex w-full items-center justify-between px-5 py-4 m3-title-small text-[var(--textPrimaryPrimary,#1b1c17)]">On this page{t15}</button>;
    $[21] = open;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== linkList || $[25] !== open) {
    t17 = open && <div className="px-3 pb-4">{linkList}</div>;
    $[24] = linkList;
    $[25] = open;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== ariaLabel || $[28] !== t11 || $[29] !== t16 || $[30] !== t17) {
    t18 = <nav aria-label={ariaLabel} className={t11}>{t16}{t17}</nav>;
    $[27] = ariaLabel;
    $[28] = t11;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  return t18;
}
function _temp3(value) {
  return !value;
}
function _temp2(a, b) {
  return a.boundingClientRect.top - b.boundingClientRect.top;
}
function _temp(entry) {
  return entry.isIntersecting;
}